import { CalendarTodayOutlined } from '@mui/icons-material'
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { map } from 'lodash'
import { useEffect, useState } from 'react'
import { ExperimentLog, ExperimentLogsRange, getExperimentLogsRanges } from '../../../api'
import { listExperimentLogs } from '../../../api/api'
import ClockIcon from '../../../assets/clock.svg'
import dayjs from '../../../utils/date'
import CustomDialog from '../../dialogs/CustomDialog/CustomDialog'
import { FormFieldLabel } from '../../form/FormFieldLabel'
import styles from './HistoryModal.module.css'

interface HistoryModalProps {
  open: boolean
  setClose: () => void
  experimentId: string
}

const isToday = (date: Date) => {
  const dateObj = dayjs(date).local()
  const today = dayjs().local()
  return dateObj.isSame(today, 'day')
}

const isThisYear = (date: Date) => {
  const dateObj = dayjs(date).local()
  const today = dayjs().local()
  return dateObj.isSame(today, 'year')
}

const formatDate = (date: Date) => {
  const dateObj = dayjs(date).local()

  if (isToday(dateObj.toDate())) {
    return dateObj.format('hh:mm A')
  } else if (isThisYear(dateObj.toDate())) {
    return dateObj.format('MMM D, hh:mm A')
  }

  return dateObj.format('MMM D, YYYY, hh:mm A')
}

export const HistoryModal = ({ open, setClose, experimentId }: HistoryModalProps) => {
  const [logRange, setLogRange] = useState<string>('all')
  const [logRangesOptions, setLogRangesOptions] = useState<ExperimentLogsRange[]>([])
  const [logs, setLogs] = useState<ExperimentLog[]>([])

  useEffect(() => {
    setLogRange('all')
    if (experimentId && open) {
      getExperimentLogsRanges(experimentId).then(res => {
        if (res) {
          setLogRangesOptions(res.ranges)
        }
      })
      listExperimentLogs({ experiment_id: experimentId, range: logRange }).then(res => {
        if (res) {
          setLogs(res.history)
        }
      })
    }
  }, [experimentId, open])

  const handleLogRangeChange = (e: SelectChangeEvent<string>) => {
    setLogRange(e.target.value)
    listExperimentLogs({ experiment_id: experimentId, range: e.target.value }).then(res => {
      if (res) {
        setLogs(res.history)
      }
    })
  }

  return (
    <CustomDialog
      open={open}
      setClose={setClose}
      title=""
      fullWidth>
      <div className={styles.historyModalContainer}>
        <div className={styles.historyModalHeader}>
          <div className={styles.historyModalHeaderTitle}>
            <img
              src={ClockIcon}
              alt="History"
            />
            <h1>History</h1>
          </div>
          <p className={styles.historyModalHeaderDescription}>Recent changes and activities on the experiment</p>
        </div>
        <div className={styles.historyModalFilter}>
          <FormFieldLabel text="Log Date" />
          <FormControl
            fullWidth
            size="small">
            <Select
              size="small"
              displayEmpty
              value={logRange}
              onChange={handleLogRangeChange}
              renderValue={value => {
                const selectedOption = logRangesOptions.find(option => option.range_key === value)
                return <span className={styles.logDateSelectValue}>{selectedOption?.label}</span>
              }}>
              {map(logRangesOptions, option => (
                <MenuItem
                  key={option.range_key}
                  value={option.range_key}
                  disabled={option.is_disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={styles.historyModalList}>
          {map(logs, log => (
            <div className={styles.historyModalListItem}>
              <p className={styles.historyModalListItemDescription}>
                <strong>{log.user_name}</strong> {log.log_description}
              </p>
              <div className={styles.historyModalListItemDate}>
                {!isToday(new Date(log.log_timestamp)) && (
                  <CalendarTodayOutlined className={styles.historyModalListItemDateIcon} />
                )}
                <p className={styles.historyModalListItemDateText}>{formatDate(new Date(log.log_timestamp))}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CustomDialog>
  )
}
